/**
 * @component CancelCorrectionDialog
 * @description
 * A confirmation dialog allowing users to **reject a correction request**
 * for a specific order.
 *
 * ---
 * **Features:**
 * - Uses Chakra UI v3.28 `Dialog` components.
 * - “Abbrechen” closes the popup without changes.
 * - “Korrektur ablehnen” sends a POST request to `/api/orders/:orderId/cancel-correction`.
 * - Displays success and error toasts using the global `toaster`.
 * - Fully responsive (>300px width) and accessible.
 *
 * **Toasts:**
 * - Success: confirms that the order was cancelled and archived.
 * - Error: explains that the order could not be cancelled.
 *
 * **Usage Example:**
 * ```tsx
 * const { open, onOpen, onClose } = useDisclosure();
 * <CancelCorrectionDialog isOpen={open} onClose={onClose} orderId="12345" />
 * ```
 */

import { Button, Dialog, Portal } from '@chakra-ui/react'
import { useState } from 'react'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { deleteOrder } from '@/api/orders.api'

interface CancelCorrectionDialogProps {
  isOpen: boolean
  onClose: () => void
  orderId: number
  onDeleted?: () => void
}

export function CancelCorrectionDialog({
  isOpen,
  onClose,
  orderId,
  onDeleted,
}: CancelCorrectionDialogProps) {
  const [isLoading, setIsLoading] = useState(false)

  const handleReject = async () => {
    setIsLoading(true)
    try {
      await deleteOrder(orderId)

      toast.success(
        'Bestellung wurde abgebrochen',
        'Der Korrekturvorschlag wurde abgelehnt und die Bestellung wurde archiviert.'
      )
      onClose()
      onDeleted?.()
    } catch (err: any) {
      toast.error(
        'Bestellung konnte nicht abgebrochen werden',
        'Bitte führen Sie den Vorgang erneut aus.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) onClose()
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Korrektur ablehnen und Bestellung abbrechen</Dialog.Title>
              <Dialog.CloseTrigger />
            </Dialog.Header>

            <Dialog.Body>
              Wenn Sie den Korrekturvorschlag ablehnen, wird die Bestellung abgebrochen und
              anschließend im Archiv angezeigt.
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button
                variant="solid"
                colorPalette="red"
                onClick={handleReject}
                loading={isLoading}
              >
                Korrektur ablehnen
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
